import { Controller } from '@hotwired/stimulus'
import { Editor } from '../bpmn/editor'

import { useApplication, useWindowResize, useDebounce } from 'stimulus-use'

export default class extends Controller {
  static debounces = ['windowResize']
  static targets = ['canvas', 'fullscreenButton']
  static values = {
    url: String,
    lintRules: Object,
    lintEnabled: {
      type: Boolean,
      default: true
    }
  }

  connect () {
    useApplication(this)
    useWindowResize(this)
    useDebounce(this)

    if (this.hasFullscreenButtonTarget && !document.fullscreenEnabled) {
      this.fullscreenButtonTarget.remove()
    }

    this.boundHandleFullscreenChange = this.handleFullscreenChange.bind(this)
    document.addEventListener('fullscreenchange', this.boundHandleFullscreenChange)
  }

  disconnect () {
    document.removeEventListener('fullscreenchange', this.boundHandleFullscreenChange)

    if (this.bpmn) {
      this.bpmn.destroy()
    }
  }

  windowResize () {
    if (this.bpmn) {
      this.bpmn.autoFit()
      this.bpmn.redrawActivties()
    }
  }

  async initializeCanvas () {
    if (this.isPreview) return
    if (!this.hasCanvasTarget) return

    if (!this.bpmn) {
      this.bpmn = new Editor({
        mode: 'viewer',
        container: this.canvasTarget,
        lintEnabled: this.lintEnabledValue,
        lintRules: this.lintRulesValue
      })

      this.bpmn.initialize()
    }

    this.attachClickHandler()

    return await this.loadDiagram()
  }

  attachClickHandler () {
    if (this.activityFrameElement) {
      this.bpmn.instance.on('element.click', ({ element }) => {
        if (element.type.endsWith('Task')) {
          this.activityFrameElement.src = this.activityFramePath(element)
        } else {
          this.closePanel()
        }
      })
    }
  }

  async loadDiagram () {
    return await this.bpmn.loadFromURL(this.urlValue)
  }

  closePanel () {
    if (!this.activityFrameElement) return

    this.activityFrameElement.removeAttribute('src')
    this.activityFrameElement.innerHTML = ''
  }

  canvasTargetConnected () {
    this.initializeCanvas()
  }

  urlValueChanged (value, previousValue) {
    if (!previousValue) return
    if (!this.bpmn) return

    this.loadDiagram()
  }

  toggleFullscreen () {
    if (document.fullscreenElement === this.element) {
      document.exitFullscreen()
    } else {
      this.element.requestFullscreen()
    }
  }

  handleFullscreenChange () {
    if (document.fullscreenElement === this.element) {
      this.enterFullscreen()
    } else {
      this.exitFullscreen()
    }
  }

  enterFullscreen () {
    this.closePanel()
    window.requestAnimationFrame(() => {
      this.bpmn.switchMode('navigated-viewer').then(() => {
        this.attachClickHandler()
      })
    })
  }

  exitFullscreen () {
    this.closePanel()
    this.bpmn.switchMode('viewer').then(() => {
      this.attachClickHandler()
    })
  }

  activityFramePath (element) {
    const { activityId, businessProcedureId } = element.businessObject

    if (activityId && businessProcedureId) {
      return `/activities/${activityId}/business_procedures/${businessProcedureId}`
    } else if (activityId) {
      return `/activities/${activityId}/business_procedures`
    } else {
      console.warn(
        'No activityId for element when trying to get activityFramePath',
        element)
    }

    return null
  }

  get activityFrameElement () {
    return document.querySelector('turbo-frame#activity')
  }
}
